/**
 * A2UI (DOM-free): the small declarative form an agent answers with — Column, Row, Text, Markdown, Button, TextField,
 * Chip, Divider — either as a turn's `a2ui` block or as a fenced ```a2ui block inside its words. The console and the
 * phone draw it with their own components from these rules, so a form reads and answers the same everywhere.
 *
 *  - {@link splitA2uiSegments}: an agent's words split into Markdown and the forms embedded in them, never raw JSON.
 *  - {@link findOptionGroups} / {@link groupOfButton} / {@link isSubmitButton}: which buttons answer which question, so
 *    a form that asks more than one thing stages each pick and sends them together.
 *  - {@link composeA2uiAnswer}: what a form sends, as the person's next message.
 *
 * Copied from the workbench's webview-ui/src/facilitator/a2uiGroups.ts (whole) and the pure half of
 * webview-ui/src/components/facilitator/MessageBlocks.tsx (parseA2uiSpec, splitA2uiSegments, the answer A2uiRenderer
 * composes), at 0.90.0.
 */
/**
 * A Button that composes field values is the form's submit, not an option.
 * `{{field}}` in its message is what makes it one.
 */
export const isSubmitButton = (node) => /\{\{[\w-]+\}\}/.test(node.action?.message ?? "");
/**
 * The question groups in a spec: each a *run* of adjacent option buttons (two or more together; a lone button is an
 * action). Anything that is not an option button — a divider, a line of text, a field — ends the run. A group is named
 * `<container path>#<first>:<last>`.
 */
export function findOptionGroups(root) {
    const groups = [];
    const walk = (node, path) => {
        const kids = node.children ?? [];
        let runStart = -1;
        let runEnd = -1;
        const closeRun = () => {
            if (runStart >= 0 && runEnd > runStart)
                groups.push(`${path}#${runStart}:${runEnd}`);
            runStart = -1;
            runEnd = -1;
        };
        kids.forEach((child, i) => {
            if (child.type === "Button" && !isSubmitButton(child)) {
                if (runStart < 0)
                    runStart = i;
                runEnd = i;
            }
            else {
                closeRun();
            }
            walk(child, `${path}.${i}`);
        });
        closeRun();
    };
    walk(root, "r");
    return groups;
}
/** The group a button at `path` belongs to, out of the known groups: only when its index falls inside that run. */
export function groupOfButton(path, groups) {
    const cut = path.lastIndexOf(".");
    if (cut < 0)
        return undefined;
    const container = path.slice(0, cut);
    const index = Number(path.slice(cut + 1));
    if (!Number.isInteger(index))
        return undefined;
    for (const group of groups) {
        const hash = group.lastIndexOf("#");
        if (group.slice(0, hash) !== container)
            continue;
        const [first, last] = group.slice(hash + 1).split(":").map(Number);
        if (first !== undefined && last !== undefined && index >= first && index <= last)
            return group;
    }
    return undefined;
}
/** A spec's root, when it is one; anything else draws nothing. */
export function a2uiRoot(spec) {
    return spec && typeof spec === "object" && !Array.isArray(spec) ? spec : null;
}
/** Whether a spec carries a submit button of its own (else a multi-question form gets a Send). */
export function hasSubmitButton(root) {
    const walk = (n) => (n.type === "Button" && isSubmitButton(n)) || (n.children ?? []).some(walk);
    return walk(root);
}
/** A button's message with `{{fieldId}}` filled from what the person typed. */
export function resolveA2uiMessage(template, values) {
    return template.replace(/\{\{([\w-]+)\}\}/g, (_, id) => values[id] ?? "");
}
/** What a form sends: the staged picks in document order, then anything typed, then `extra`, one per line. */
export function composeA2uiAnswer(groupPaths, choices, values, extra) {
    const parts = groupPaths.map((g) => choices[g]?.message).filter((m) => !!m);
    for (const v of Object.values(values)) {
        const typed = v.trim();
        if (typed && !parts.includes(typed))
            parts.push(typed);
    }
    const tail = extra?.trim();
    if (tail && !parts.includes(tail))
        parts.push(tail);
    return parts.join("\n");
}
/**
 * Parse an a2ui payload, repairing the two ways models routinely break it: a trailing comma before a closing bracket,
 * and closing brackets missing because the model stopped early. Nothing is invented and nothing existing rewritten.
 */
export function parseA2uiSpec(raw) {
    const attempt = (text) => {
        try {
            return { ok: true, spec: JSON.parse(text) };
        }
        catch {
            return undefined;
        }
    };
    const trimmed = raw.trim();
    if (!trimmed)
        return { ok: false };
    const direct = attempt(trimmed);
    if (direct)
        return direct;
    const stack = [];
    let inString = false;
    let escaped = false;
    for (const ch of trimmed) {
        if (escaped) {
            escaped = false;
            continue;
        }
        if (inString) {
            if (ch === "\\")
                escaped = true;
            else if (ch === '"')
                inString = false;
            continue;
        }
        if (ch === '"')
            inString = true;
        else if (ch === "{" || ch === "[")
            stack.push(ch === "{" ? "}" : "]");
        else if (ch === "}" || ch === "]")
            stack.pop();
    }
    let repaired = trimmed;
    if (inString)
        repaired += '"';
    repaired = repaired.replace(/,\s*$/, "");
    while (stack.length)
        repaired += stack.pop();
    return attempt(repaired) ?? { ok: false };
}
/** Split an agent's words into Markdown and embedded ```a2ui segments; the closing fence is optional. */
export function splitA2uiSegments(text) {
    const segments = [];
    const fence = /```a2ui[^\S\n]*\n?([\s\S]*?)(?:```|$)/g;
    let last = 0;
    let match;
    while ((match = fence.exec(text)) !== null) {
        if (match.index > last)
            segments.push({ kind: "markdown", text: text.slice(last, match.index) });
        const parsed = parseA2uiSpec(match[1] ?? "");
        segments.push(parsed.ok ? { kind: "a2ui", spec: parsed.spec } : { kind: "a2ui-broken" });
        last = match.index + match[0].length;
        if (match[0].length === 0)
            fence.lastIndex++;
    }
    if (last < text.length)
        segments.push({ kind: "markdown", text: text.slice(last) });
    return segments;
}
/** Shown in place of a form whose JSON could not be recovered. */
export const A2UI_BROKEN = "The agent sent an interactive form that could not be shown here. Answer it in words instead.";
//# sourceMappingURL=a2uiModel.js.map